/**
 * IceCartel Wishlist v2  - wishlist.js
 * ─────────────────────────────────────────────────────
 * Guest      → localStorage (temporary)
 * Logged in  → Shopify customer metafield (permanent)
 * On login   → merges localStorage into metafield
 */

(function () {
  'use strict';

  const STORAGE_KEY   = 'ic_wishlist';
  const METAFIELD_NS  = 'custom';
  const METAFIELD_KEY = 'wishlist';

  const customer      = window.__IC_CUSTOMER__ || {};
  const IS_LOGGED_IN  = customer.loggedIn === true;
  const STOREFRONT_TOKEN = customer.storefrontToken || '';
  const SHOP_DOMAIN   = window.Shopify?.shop || window.location.hostname;

  /* ═══════════════════════════════════════════════════════
     SECTION 1 — Storage layer (abstracts guest vs logged-in)
     ═══════════════════════════════════════════════════════ */

  // ── Guest: localStorage ───────────────────────────────

  function localGet() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; }
    catch { return []; }
  }

  function localSet(list) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  }

  function localClear() {
    localStorage.removeItem(STORAGE_KEY);
  }

  // ── Logged-in: Shopify Storefront API ─────────────────

  async function apiQuery(query, variables = {}) {
    const res = await fetch(`https://${SHOP_DOMAIN}/api/2024-01/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
      },
      body: JSON.stringify({ query, variables }),
    });
    const json = await res.json();
    if (json.errors) throw new Error(json.errors[0].message);
    return json.data;
  }

  // Read the wishlist metafield from Shopify
  async function metafieldGet() {
    try {
      const data = await apiQuery(`
        query {
          customer(customerAccessToken: "${await getAccessToken()}") {
            metafield(namespace: "${METAFIELD_NS}", key: "${METAFIELD_KEY}") {
              value
            }
          }
        }
      `);
      const raw = data?.customer?.metafield?.value;
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  // Write the wishlist array to Shopify
  async function metafieldSet(list) {
    try {
      await apiQuery(`
        mutation customerUpdate($input: CustomerUpdateInput!) {
          customerUpdate(input: $input) {
            userErrors { field message }
          }
        }
      `, {
        input: {
          metafields: [{
            namespace: METAFIELD_NS,
            key: METAFIELD_KEY,
            value: JSON.stringify(list),
            type: 'json',
          }]
        }
      });
    } catch (e) {
      console.warn('Wishlist metafield write failed:', e);
    }
  }

  // ── Unified API used by the rest of the code ──────────

  let _cachedList = null; // in-memory cache to avoid repeated API calls

  async function getList() {
    if (_cachedList) return _cachedList;
    _cachedList = IS_LOGGED_IN ? await metafieldGet() : localGet();
    return _cachedList;
  }

  async function saveList(list) {
    _cachedList = list;
    if (IS_LOGGED_IN) {
      await metafieldSet(list);
    } else {
      localSet(list);
    }
  }

  async function isWishlisted(productId) {
    const list = await getList();
    return list.some((p) => String(p.id) === String(productId));
  }

  async function addToList(product) {
    const list = await getList();
    if (!list.some((p) => String(p.id) === String(product.id))) {
      list.unshift(product);
      await saveList(list);
    }
  }

  async function removeFromList(productId) {
    const list = await getList();
    await saveList(list.filter((p) => String(p.id) !== String(productId)));
  }

  async function toggleItem(product) {
    const already = await isWishlisted(product.id);
    if (already) {
      await removeFromList(product.id);
      return false;
    } else {
      await addToList(product);
      return true;
    }
  }

  /* ═══════════════════════════════════════════════════════
     SECTION 2 — Customer access token
     (Shopify stores this in a cookie after login)
     ═══════════════════════════════════════════════════════ */

  // function getAccessToken() {
  //   // Shopify sets _shopify_y cookie and stores the token
  //   // in localStorage under a key like `_shopify_sa_t` or
  //   // the theme may expose it. Most reliable: read from meta tag.
  //   const meta = document.querySelector('meta[name="shopify-customer-token"]');
  //   if (meta) return meta.content;

  //   // Fallback: check common localStorage keys Shopify uses
  //   const keys = Object.keys(localStorage).filter(k => k.includes('customer_access_token') || k.includes('shopify_token'));
  //   if (keys.length) return localStorage.getItem(keys[0]);

  //   return '';
  // }

  function getAccessToken() {
    return window.__IC_CUSTOMER__?.accessToken || '';
  }
  /* ═══════════════════════════════════════════════════════
     SECTION 3 — Merge guest wishlist into account on login
     ═══════════════════════════════════════════════════════ */

  async function mergeGuestWishlist() {
    if (!IS_LOGGED_IN) return;

    const guestList = localGet();
    if (!guestList.length) return;

    const serverList = await metafieldGet();

    // Merge: add guest items not already on server
    let changed = false;
    for (const item of guestList) {
      if (!serverList.some((p) => String(p.id) === String(item.id))) {
        serverList.unshift(item);
        changed = true;
      }
    }

    if (changed) {
      await metafieldSet(serverList);
      _cachedList = serverList;
    }

    // Clear localStorage now that it's safely on the server
    localClear();
  }

  /* ═══════════════════════════════════════════════════════
     SECTION 4 — UI: Heart button
     ═══════════════════════════════════════════════════════ */

  function getProductFromPage() {
    const dataEl = document.querySelector('[data-product-json]');
    if (!dataEl) return null;
    try {
      const raw = JSON.parse(dataEl.textContent);
      return {
        id: raw.id,
        title: raw.title,
        handle: raw.handle,
        url: '/products/' + raw.handle,
        image: raw.featured_image,
        price: raw.variants?.[0]?.price ?? 0,
        compare_at_price: raw.variants?.[0]?.compare_at_price ?? 0,
      };
    } catch { return null; }
  }

  async function mountHeartButtons() {
    const buttons = document.querySelectorAll('.ic-wishlist-heart[data-wishlist-id]');

    if (!buttons.length) return;

    for (const btn of buttons) {

      if (btn.dataset.wishlistMounted) continue;
      btn.dataset.wishlistMounted = '1';

      let product = {
        id: btn.dataset.wishlistId,
        title: btn.dataset.wishlistTitle,
        handle: btn.dataset.wishlistHandle,
        url: btn.dataset.wishlistUrl,
        image: btn.dataset.wishlistImage,
        price: Number(btn.dataset.wishlistPrice || 0),
        compare_at_price: Number(btn.dataset.wishlistCompareAtPrice || 0),
      };

      if (!product.handle) {
        const pageProduct = getProductFromPage();

        if (pageProduct && String(pageProduct.id) === String(product.id)) {
          product = {
            ...pageProduct,
            ...product,
            id: pageProduct.id,
            title: pageProduct.title,
            handle: pageProduct.handle,
            url: pageProduct.url,
            image: pageProduct.image,
            price: pageProduct.price,
            compare_at_price: pageProduct.compare_at_price,
          };
        }
      }

      async function refresh() {
        const active = await isWishlisted(product.id);

        btn.classList.toggle('is-wishlisted', active);

        btn.setAttribute(
          'aria-label',
          active
            ? 'Remove from wishlist'
            : 'Add to wishlist'
        );
      }

      await refresh();

      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const nowActive = btn.classList.toggle('is-wishlisted');

        btn.setAttribute(
          'aria-label',
          nowActive
            ? 'Remove from wishlist'
            : 'Add to wishlist'
        );

        btn.classList.remove('ic-heart-pop');
        void btn.offsetWidth;
        btn.classList.add('ic-heart-pop');

        const added = await toggleItem(product);

        btn.classList.toggle('is-wishlisted', added);

        showToast(
          added
            ? '❤️ Added to wishlist'
            : 'Removed from wishlist'
        );

        updateHeaderBadge();
      });
    }
  }

  /* ═══════════════════════════════════════════════════════
     SECTION 5 — UI: Toast, Badge, Header icon, Drawer
     (same as v1 — no changes needed here)
     ═══════════════════════════════════════════════════════ */

  let toastTimer;
  function showToast(msg) {
    let toast = document.getElementById('ic-wishlist-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'ic-wishlist-toast';
      document.body.appendChild(toast);
    }
    toast.textContent = msg;
    toast.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 2200);
  }

  async function updateHeaderBadge() {
    const list = await getList();
    const count = list.length;
    // Target the specific badge rendered by Liquid
    const badge = document.getElementById('ic-wishlist-badge-header');
    if (badge) {
      // badge.textContent = count;
      badge.style.display = count > 0 ? 'flex' : 'none';
    }
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  /* ═══════════════════════════════════════════════════════
     SECTION 6 — Init
     ═══════════════════════════════════════════════════════ */

  async function init() {

    // If just logged in, merge guest wishlist into account
    await mergeGuestWishlist();

    await updateHeaderBadge();
    await mountHeartButtons();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.ICWishlist = { open: getList, isWishlisted };
})();